// Presentation-only context from explicitly labelled response fields already
// loaded with the report. Never mine AI interpretations for client quotations.
const plain = value => {
  const doc = new DOMParser().parseFromString(String(value || '').replace(/<br\s*\/?>/gi, '\n'), 'text/html');
  return doc.body.textContent;
};
export const reportSourceResponses = (points = []) => {
  const responses = [];
  const add = value => {
    const text = value.replace(/\*\*/g, '').replace(/\s+/g, ' ').trim();
    if (text && !/^(?:time out|timeout|undisclosed|no response)$/i.test(text)) responses.push(text);
  };
  points.forEach(point => {
    const text = plain(point.content);
    if (point.point_number === 11) {
      for (const block of text.split('Client Responses:').slice(1)) {
        for (const match of block.split(/(?:Average )?Time Taken:/)[0].matchAll(/^\s*•\s*(.*?)\s*\((?:Positive|Negative|Neutral|Time Out)\)/gm)) add(match[1]);
      }
    }
    if (point.point_number === 14) for (const m of text.matchAll(/^Response:\s*(.+)$/gm)) add(m[1]);
    if (point.point_number === 13) for (const m of text.matchAll(/(?:\*\*)?Client['’]s Response:(?:\*\*)?\s*([^\n]+)/g)) add(m[1].replace(/\b[A-E]\)\s*/g, ''));
    if (point.point_number === 1) for (const m of text.matchAll(/(?:comes seeking help for:|reports being irritated by the following:|aspirations include:|faces certain challenges:|About lucid dreams:)\s*([^\n]+)/g)) {
      add(m[1].replace(/\. However,.*$/, '').replace(/\.+$/, ''));
    }
  });
  return [...new Set(responses)].sort((a, b) => b.length - a.length);
};
export const RESPONSE_INK = '#115e59';
export const REFERENCE_INK = '#1e3a8a';
const escape = value => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
const word = /[\p{L}\p{N}_]/u;
const references = /\[(?=[^\]\n]*(?:\bPoints?\s+\d|\bPhase\s+\d|\bWhy\s+(?:Patterns?|Section)|\bNext\s+Steps?\b|\bNexus\b))[^\]\n]+\]|\b(?:Why\s+Patterns?|Next\s+Steps?\s+Experiment)\s+(?:\d+|One|Two|Three|Four|Five|Six|Seven|Eight|Nine|Ten)\b/gi;

// Ranges are split across existing inline nodes without moving text or links.
export const highlightReportEvidence = (root, excluded, responses = []) => {
  const doc = root.ownerDocument;
  const responsePattern = responses.length ? new RegExp(responses.map(v => escape(v).replace(/\s+/g, '\\s+')).join('|'), 'gu') : null;
  let nodes = [];
  const flush = () => {
    let text = '';
    const entries = nodes.map(node => { const start = text.length; text += node.nodeValue; return { node, start, end: text.length }; });
    const ranges = [...text.matchAll(references)].map(m => ({ start: m.index, end: m.index + m[0].length, className: 'report-reference' }));
    if (responsePattern) for (const m of text.matchAll(responsePattern)) {
      const start = m.index, end = start + m[0].length;
      if (word.test(text[start - 1] || '') || word.test(text[end] || '')) continue;
      // A matching phrase in proposed dialogue remains proposed dialogue.
      const before = text.slice(0, start);
      const context = entries.find(e => e.end > start)?.node.parentElement.closest('.report-field')?.querySelector('.phase5-field-label')?.textContent || '';
      const sentence = before.split(/[.!?]["'”’]?\s+/).pop();
      if (/\b(?:possible client wording|suggested (?:dialogue|response|wording)|hypothetical|response script|role.?play|you could say|might say|could say|would say|paraphrase)\b/i.test(context + ' ' + sentence)) continue;
      const quoted = /[“"‘']\s*$/.test(before) && /^\s*[.!?,;:]?[”"’']/.test(text.slice(end));
      const attributed = /\b(?:responded|response|reported|stated|described|named|said|wrote|grounded in)\b/i.test(sentence);
      if (!quoted && !attributed) continue;
      if (!ranges.some(r => start < r.end && end > r.start)) ranges.push({ start, end, className: 'report-user-response' });
    }
    ranges.filter(r => !entries.some(e => e.end > r.start && e.start < r.end && e.node.parentElement.closest('.report-reference,.report-user-response'))).sort((a,b) => b.start - a.start).forEach(({ start, end, className }) => {
      entries.filter(e => e.end > start && e.start < end).reverse().forEach(e => {
        const selected = e.node.splitText(Math.max(start, e.start) - e.start);
        selected.splitText(Math.min(end, e.end) - Math.max(start, e.start));
        const span = doc.createElement('span'); span.className = className;
        selected.replaceWith(span); span.append(selected);
      });
    });
    nodes = [];
  };
  const walk = node => {
    if (node.nodeType === 3) { nodes.push(node); return; }
    if (node.nodeType !== 1) return;
    if (node.matches(excluded)) { flush(); return; }
    const block = /^(P|DIV|LI|TD|TH|BLOCKQUOTE|UL|OL|TABLE|BR)$/.test(node.tagName) || node.matches('.report-field,.report-field-body');
    if (block) flush();
    Array.from(node.childNodes).forEach(walk);
    if (block) flush();
  };
  walk(root); flush();
};
